import { AnimatePresence, motion } from "framer-motion";
import { Check, Swords } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ACTIONS, ROLES } from "../gameData";
import type { GameState, Player, Role } from "../types";
import { RoleCard } from "./RoleCard";

/** Longest player name allowed inside an event headline before it is elided. */
const NAME_LIMIT = 13;

export function shortName(name: string | undefined, fallback: string) {
  const value = name ?? fallback;
  return value.length > NAME_LIMIT ? `${value.slice(0, NAME_LIMIT - 1)}…` : value;
}

interface InterferenceProps {
  game: GameState;
  me: Player;
  onPass: () => Promise<boolean>;
  onChallenge: () => Promise<boolean>;
  onBlock: (role: Role) => Promise<boolean>;
}

export function InterferencePanel({
  game,
  me,
  onPass,
  onChallenge,
  onBlock
}: InterferenceProps) {
  const [submitting, setSubmitting] = useState(false);
  const [expired, setExpired] = useState(false);
  const action = game.action ? ACTIONS[game.action.type] : null;
  const actor = game.players.find((player) => player.id === game.action?.actorId);
  const target = game.players.find((player) => player.id === game.action?.targetId);
  const claimant = game.players.find((player) => player.id === game.window?.claimantId);
  const claimRole = game.window?.claimRole ? ROLES[game.window.claimRole] : null;
  /** The one window a declared action opens. "claim" is only ever a block. */
  const isResponse = game.phase === "response_window";
  const eventText = describeEvent(game, actor, target, claimant);

  // Eligibility is NOT one set. `eligibleIds` is the union — everyone who must
  // answer for the window to close — but the two answers it can be closed with
  // have different rosters:
  //   DOUBT  any living opponent, whenever the actor claimed a role.
  //   BLOCK  every opponent on Foreign Aid; the TARGET ALONE on Steal and
  //          Assassinate.
  // A bystander on a Steal is therefore eligible for the window and eligible to
  // doubt, but must not be offered a block. One Pass surrenders both.
  const eligible = Boolean(game.window?.eligibleIds.includes(me.id));
  const passed = Boolean(game.window?.passes.includes(me.id));
  // A block declared in this window is HELD while the doubt against the actor
  // settles, so the window outlives it. Two consequences the panel must honour:
  // the blocker has spent their answer and gets no live controls, and everyone
  // still deciding can see the block that is already on the table.
  const heldBlockRole = game.window?.blockRole ?? null;
  const blocker = game.players.find((player) => player.id === game.window?.blockerId);
  const iBlocked = Boolean(game.window?.blockerId) && game.window?.blockerId === me.id;
  // Once the bar empties the server auto-passes for everyone still silent.
  // The client only REFLECTS that — it never submits a pass of its own, so
  // there is no double submission. The controls stay live but go lapsed: the
  // bar can only run behind the true deadline (msLeft is already stale by one
  // network hop), so a tap that still lands is a window the server considers
  // open, and a player is never stranded if the sweep is late.
  const canRespond = eligible && !passed && !iBlocked;
  const canDoubt =
    canRespond &&
    Boolean(game.window?.claimRole) &&
    (game.window?.challengeIds ?? []).includes(me.id);
  const blockRoles = canRespond && (game.window?.blockIds ?? []).includes(me.id)
    ? (game.window?.allowedRoles ?? [])
    : [];
  const canBlock = blockRoles.length > 0;
  // Foreign Aid is the only shape with a block and no doubt, and it claims
  // exactly one role. That role takes the slot Doubt would have occupied — "a
  // little duke where the doubt otherwise would have been". Every other
  // blockable action also offers a doubt, so its blocks go beneath the pair.
  const blockInDoubtSlot = canBlock && !canDoubt && blockRoles.length === 1;
  const blockBeneath = canBlock && !blockInDoubtSlot;

  // The server owns the clock. durationMs is the denominator, msLeft is the
  // remaining time at the instant this snapshot was sent — anchor it against
  // performance.now(), never the device clock. Either may be missing on an
  // older server, in which case no bar is drawn and nothing ever locks.
  const durationMs = game.window?.durationMs ?? 0;
  const msLeft = game.window?.msLeft ?? 0;
  const hasTimer =
    Number.isFinite(durationMs) && durationMs > 0 && Number.isFinite(msLeft);

  // Identity of the current window: changes whenever a new question is asked.
  const windowKey = [
    game.phase,
    game.action?.id ?? "action",
    game.window?.source ?? "action",
    game.window?.claimantId ?? "open",
    game.window?.claimRole ?? "none"
  ].join("|");

  useEffect(() => {
    setSubmitting(false);
    setExpired(false);
  }, [windowKey]);

  // Keyed off the window, not the phase: a response window carries a claim for
  // every action except Foreign Aid, which claims nothing and so has nothing to
  // doubt.
  const eventDetail =
    game.window?.source === "block"
      ? "The block can be challenged"
      : claimRole
        ? // A claim proven against a failed doubt cannot be doubted twice, and
          // the window only reopened to collect the block it still owes. Saying
          // "claims Assassin" here would invite a doubt that no longer exists.
          game.window?.claimProven
          ? `${claimRole.name} proven · block or pass`
          : `${shortName(claimant?.name, "A player")} ${
              claimant?.name.toLowerCase() === "you" ? "claim" : "claims"
            } ${claimRole.name}`
        : canRespond
          ? "You can interfere"
          : "Waiting for responses";

  if (!["response_window", "claim_window"].includes(game.phase) || !game.window || !action) {
    return null;
  }

  const statusText = iBlocked
    ? "You blocked · the claim settles first"
    : passed
      ? "You passed · resolving…"
      : expired
        ? "Time up · resolving…"
        : eventDetail;

  const submit = async (response: () => Promise<boolean>) => {
    if (submitting) return;
    setSubmitting(true);
    const accepted = await response();
    if (!accepted) setSubmitting(false);
  };

  const holds = (role: Role) =>
    me.influence.some((card) => !card.revealed && card.role === role);

  return (
    <motion.div
      className={[
        "interference-event",
        isResponse ? "interference-event--response" : "interference-event--claim",
        expired ? "interference-event--lapsed" : "",
        // The one state whose controls do not fit a landscape phone: the panel
        // grows downward from `.court-center`'s fixed top and the stage clips
        // it, so this row is the only one that can leave the viewport. Named so
        // the stylesheet can reshape exactly that case without a :has() whose
        // specificity has to be reconciled with --response and --lapsed.
        blockBeneath ? "interference-event--blocking" : ""
      ]
        .filter(Boolean)
        .join(" ")}
      key={`${game.phase}-${game.action?.id ?? "action"}-${game.window.claimantId ?? "open"}`}
      initial={false}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* One grid, three occupants of column 3: DOUBT, or the lone blocking
          role standing in for it, or nothing at all when the player has
          already answered. */}
      <div className="interference-event__main">
        {canRespond && (
          <motion.button
            className="interference-answer interference-answer--pass"
            type="button"
            disabled={submitting}
            onClick={() => submit(onPass)}
            whileTap={{ scale: 0.94 }}
          >
            <Check size={17} strokeWidth={2.6} />
            <span>Pass</span>
          </motion.button>
        )}

        <div className="interference-event__copy">
          <h2 className="interference-event__headline">{eventText}</h2>
          <p>{statusText}</p>
        </div>

        {canDoubt && (
          <motion.button
            className="interference-answer interference-answer--challenge"
            type="button"
            disabled={submitting}
            onClick={() => submit(onChallenge)}
            whileTap={{ scale: 0.94 }}
          >
            <Swords size={17} strokeWidth={2.6} />
            <span>Doubt</span>
          </motion.button>
        )}

        {blockInDoubtSlot &&
          (() => {
            const role = blockRoles[0];
            const held = holds(role);
            return (
              <motion.button
                className="interference-answer interference-answer--block"
                type="button"
                disabled={submitting}
                onClick={() => submit(() => onBlock(role))}
                whileTap={{ scale: 0.94 }}
                style={{ "--role-color": ROLES[role].color } as React.CSSProperties}
                title={ROLES[role].name}
                aria-label={`Block with ${ROLES[role].name}${held ? ", held" : ", bluff"}`}
              >
                <img className="interference-answer__face" src={ROLES[role].image} alt="" />
                <span>Block</span>
                {!held && <em className="interference-answer__bluff">BLUFF</em>}
              </motion.button>
            );
          })()}
      </div>

      {/* The block row and its held-block twin come BEFORE the bar, and that
          ordering is load-bearing rather than taste. `.court-center` is pinned
          to a fixed top and `.table-stage` clips, so this panel is only ever
          cut off from the BOTTOM — last in the DOM is first to be lost. In
          landscape (667x375) the block buttons used to sit at y371-417 in a
          375px viewport: zero hit-testable pixels, so a Steal target holding
          the phone sideways could not block at all. The bar is a readout and
          survives being clipped as information; the buttons are the only way to
          answer, so they take the higher ground and the clock takes the risk.
          Nothing else moves — a viewer with no block offered still sees the bar
          immediately under the answers, exactly as before.

          The strip below is mutually exclusive with those buttons: the server
          withdraws the block offer the moment one lands, so it reuses the same
          slot to say what is already on the table while the doubt track runs. */}
      {heldBlockRole && (
        <div
          className="held-block"
          style={{ "--role-color": ROLES[heldBlockRole].color } as React.CSSProperties}
        >
          <img src={ROLES[heldBlockRole].image} alt="" />
          <span>
            <b>
              {shortName(blocker?.name, "A player")}{" "}
              {blocker?.name.toLowerCase() === "you" ? "block" : "blocks"} as{" "}
              {ROLES[heldBlockRole].name}
            </b>
            <small>The claim settles first</small>
          </span>
        </div>
      )}

      {blockBeneath && (
        <div className="inline-block-roles">
          {blockRoles.map((role) => {
            const held = holds(role);
            return (
              <button
                className="inline-block-role"
                type="button"
                key={role}
                disabled={submitting}
                onClick={() => submit(() => onBlock(role))}
                style={{ "--role-color": ROLES[role].color } as React.CSSProperties}
                aria-label={`Block with ${ROLES[role].name}${held ? ", held" : ", bluff"}`}
              >
                <img src={ROLES[role].image} alt="" />
                <span>
                  <b>BLOCK</b>
                  <small>{ROLES[role].name}</small>
                </span>
                {!held && <em className="inline-block-role__bluff">BLUFF</em>}
              </button>
            );
          })}
        </div>
      )}

      {hasTimer && (
        <WindowTimer
          durationMs={durationMs}
          msLeft={msLeft}
          resetKey={windowKey}
          onEmpty={() => setExpired(true)}
        />
      )}
    </motion.div>
  );
}

interface TimerProps {
  durationMs: number;
  msLeft: number;
  resetKey: string;
  onEmpty: () => void;
}

/**
 * Depleting bar for every deadline the server keeps: the response/claim windows
 * and the pending loss, which also auto-resolves. Paints straight to the DOM on
 * rAF so a ticking clock never re-renders the panel underneath it.
 */
export function WindowTimer({ durationMs, msLeft, resetKey, onEmpty }: TimerProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const fillRef = useRef<HTMLSpanElement | null>(null);
  const emptyRef = useRef(onEmpty);

  useEffect(() => {
    emptyRef.current = onEmpty;
  });

  useEffect(() => {
    const total = durationMs;
    const begin = Math.max(0, Math.min(msLeft, total));
    const startedAt = performance.now();
    let frame = 0;
    let urgent: boolean | null = null;
    let finished = false;

    const paint = () => {
      const remaining = Math.max(0, begin - (performance.now() - startedAt));
      const ratio = total > 0 ? remaining / total : 0;
      if (fillRef.current) {
        fillRef.current.style.transform = `scaleX(${ratio})`;
      }
      const nextUrgent = ratio <= 0.25;
      if (nextUrgent !== urgent) {
        urgent = nextUrgent;
        if (trackRef.current) {
          trackRef.current.dataset.urgent = nextUrgent ? "true" : "false";
        }
      }
      if (remaining <= 0) {
        if (!finished) {
          finished = true;
          emptyRef.current();
        }
        return;
      }
      frame = requestAnimationFrame(paint);
    };

    paint();
    return () => cancelAnimationFrame(frame);
  }, [durationMs, msLeft, resetKey]);

  return (
    <div className="interference-timer" ref={trackRef} aria-hidden="true">
      <span className="interference-timer__fill" ref={fillRef} />
    </div>
  );
}

interface DecisionProps {
  game: GameState;
  me: Player;
  onLose: (cardId: string) => void;
  onExchange: (keepIds: string[]) => void;
}

export function DecisionPanel({ game, me, onLose, onExchange }: DecisionProps) {
  const [kept, setKept] = useState<string[]>([]);
  const [expired, setExpired] = useState(false);
  // Kept apart from `expired` above rather than sharing one flag: a doubted
  // Exchange queues a loss and an exchange back to back on the same action id,
  // so one flag with one reset key would carry the loss's lapse into the sort.
  const [exchangeExpired, setExchangeExpired] = useState(false);
  const hiddenCount = me.influence.filter((card) => !card.revealed).length;
  const exchangeCards = game.exchangeChoices?.cards ?? [];
  const pendingLoss = game.pendingLoss;
  const challenger = game.players.find((player) => player.id === pendingLoss?.challengerId);
  const claimant = game.players.find((player) => player.id === pendingLoss?.claimantId);
  const claimRole = pendingLoss?.claimRole ? ROLES[pendingLoss.claimRole] : null;
  // Branch on `kind`, never on the cause string. Both halves of a settled doubt
  // end here — the bluffer caught out, or the doubter who was wrong — and the
  // server hands each of them their own true shape while every bystander gets
  // "callout" with the verdict withheld. So arriving here with either kind is
  // already proof this seat is the one paying. Prefix-matching the cause would
  // break the moment the wording changed, and `cause` is deliberately not the
  // same string for every viewer, so it can never be a branch.
  const caughtBluffing = pendingLoss?.kind === "bluff";
  const doubtFailed = pendingLoss?.kind === "failed_challenge";
  // Both masked kinds carry the same promise: the table has been told who
  // doubted whom and what was claimed, and NOT how it went. Whoever turns a
  // card is the one who hands the room the answer, and should know it.
  const lossCallout = caughtBluffing
    ? challenger
      ? `${challenger.name} doubted your ${claimRole?.name ?? "claim"}`
      : null
    : doubtFailed
      ? claimant
        ? `${claimant.name} held the ${claimRole?.name ?? "claim"}`
        : null
      : null;
  const lossCopy = caughtBluffing
    ? "Nobody else knows yet. The table learns the truth the moment you turn a card."
    : doubtFailed
      ? "The table sees only a doubt in progress. They learn the claim was good the moment you turn a card."
      : pendingLoss?.cause;

  // The server owns this clock too — a pending loss auto-surrenders — so the
  // bar is read exactly as the response window reads its own: durationMs is the
  // denominator, msLeft the remainder at send time, anchored against
  // performance.now(). Either missing means an older server: no bar, no lapse.
  const lossDurationMs = pendingLoss?.durationMs ?? 0;
  const lossMsLeft = pendingLoss?.msLeft ?? 0;
  const hasLossTimer =
    Number.isFinite(lossDurationMs) && lossDurationMs > 0 && Number.isFinite(lossMsLeft);
  // Identity of this particular debt. An assassination that is also doubted
  // queues two losses back to back; without a key that moves between them the
  // second would inherit the first's spent bar and open already lapsed.
  const lossKey = [
    game.action?.id ?? game.turn,
    pendingLoss?.kind ?? "none",
    pendingLoss?.claimantId ?? "none",
    pendingLoss?.challengerId ?? "none"
  ].join("|");

  useEffect(() => {
    setExpired(false);
  }, [lossKey]);

  // The Exchange has a clock too, and it is the one deadline in the game a
  // player is most likely to run out: four cards to read, two of them never seen
  // before, and on lapse the server submits the hidden cards already held — the
  // two drawn are handed straight back. Sorting in silence with no bar was the
  // only decision the server times that the client did not show.
  //
  // Read exactly as the other two are: durationMs is the denominator, msLeft the
  // remainder at send time, anchored against performance.now(). Both are
  // optional on the wire, so either missing means no bar and no lapse.
  const exchangeDurationMs = game.exchangeChoices?.durationMs ?? 0;
  const exchangeMsLeft = game.exchangeChoices?.msLeft ?? 0;
  const hasExchangeTimer =
    Number.isFinite(exchangeDurationMs) &&
    exchangeDurationMs > 0 &&
    Number.isFinite(exchangeMsLeft);
  // Identity of this particular sort. The seat is in the key as well as the
  // action because a lapsed Exchange ends the turn and the next player's
  // Exchange can carry the same fallback (`game.turn`) if the action id is ever
  // missing — without the seat the second bar would open already spent.
  const exchangeKey = [
    game.action?.id ?? game.turn,
    game.exchangeChoices?.playerId ?? "none"
  ].join("|");

  useEffect(() => {
    setExchangeExpired(false);
  }, [exchangeKey]);

  const panel = useMemo(() => {
    if (game.phase === "lose_influence" && game.pendingLoss?.playerId === me.id) {
      return "loss";
    }
    if (game.phase === "exchange" && game.exchangeChoices?.playerId === me.id) {
      return "exchange";
    }
    return null;
  }, [game, me.id]);

  if (!panel) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.section
        className={`decision-panel decision-panel--${panel}`}
        key={`${panel}-${game.action?.id ?? game.turn}`}
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12 }}
      >
        {panel === "loss" && (
          <>
            {lossCallout && (
              <motion.div
                className="loss-callout"
                initial={{ opacity: 0, y: 10, rotate: -1 }}
                animate={{ opacity: 1, y: 0, rotate: -2 }}
              >
                <Swords size={17} />
                <b>{lossCallout}</b>
              </motion.div>
            )}
            <span className="eyebrow">
              {caughtBluffing
                ? "Caught bluffing"
                : doubtFailed
                  ? "Your doubt failed"
                  : "Influence at stake"}
            </span>
            <h2>Choose what you lose</h2>
            <p>{lossCopy}</p>
            {hasLossTimer && (
              <WindowTimer
                durationMs={lossDurationMs}
                msLeft={lossMsLeft}
                resetKey={lossKey}
                onEmpty={() => setExpired(true)}
              />
            )}
            {/* Lapsed, never surrendered on the player's behalf: the bar can
                only run behind the true deadline, so a tap that still lands is
                a debt the server considers open. The cards stay live. */}
            <p className="decision-hint">
              {expired
                ? "Time is up — the chamber is turning a card for you."
                : "Hold a card to read what it does."}
            </p>
            <div className="decision-cards">
              {me.influence
                .filter((card) => !card.revealed)
                .map((card) => (
                  <RoleCard
                    key={card.id}
                    card={card}
                    variant="loss"
                    selectable
                    onSelect={() => onLose(card.id)}
                  />
                ))}
            </div>
          </>
        )}

        {panel === "exchange" && (
          <>
            <span className="eyebrow">The Ambassador returns</span>
            <h2>Keep {hiddenCount}</h2>
            <p>Tap {hiddenCount} to keep.</p>
            {hasExchangeTimer && (
              <WindowTimer
                durationMs={exchangeDurationMs}
                msLeft={exchangeMsLeft}
                resetKey={exchangeKey}
                onEmpty={() => setExchangeExpired(true)}
              />
            )}
            {/* Lapsed, never submitted on the player's behalf — the same trade
                the loss panel makes. The bar can only run BEHIND the true
                deadline (msLeft was already one network hop stale when it
                arrived), so a selection that still lands is one the server
                considers open. The cards and the button stay live. */}
            <p className="decision-hint">
              {exchangeExpired
                ? "Time is up — the chamber is keeping the cards you already hold."
                : "Hold a card to read what it does."}
            </p>
            <div className="exchange-grid">
              {exchangeCards.map((card) => (
                <RoleCard
                  key={card.id}
                  card={card}
                  variant="exchange"
                  selectable
                  selected={kept.includes(card.id)}
                  onSelect={() =>
                    setKept((current) =>
                      current.includes(card.id)
                        ? current.filter((id) => id !== card.id)
                        : current.length < hiddenCount
                          ? [...current, card.id]
                          : current
                    )
                  }
                />
              ))}
            </div>
            <button
              className="button button--primary button--full"
              type="button"
              disabled={kept.length !== hiddenCount}
              onClick={() => {
                onExchange(kept);
                setKept([]);
              }}
            >
              Keep these cards
            </button>
          </>
        )}
      </motion.section>
    </AnimatePresence>
  );
}

function describeEvent(
  game: GameState,
  actor: Player | undefined,
  target: Player | undefined,
  claimant: Player | undefined
) {
  if (game.window?.source === "block") {
    const role = game.window.claimRole ? ROLES[game.window.claimRole].name : "a role";
    const isYou = (claimant?.name ?? "").toLowerCase() === "you";
    const subject = shortName(claimant?.name, "A player");
    return `${subject} ${isYou ? "block" : "blocks"} as ${role}`;
  }

  const isYou = (actor?.name ?? "").toLowerCase() === "you";
  const subject = shortName(actor?.name, "A player");
  const victim = shortName(target?.name, "a rival");
  switch (game.action?.type) {
    case "patronage":
      return `${subject} ${isYou ? "take" : "takes"} Foreign Aid`;
    case "levy":
      return `${subject} ${isYou ? "take" : "takes"} Tax`;
    case "exchange":
      return `${subject} ${isYou ? "exchange" : "exchanges"} Influence`;
    case "seize":
      return `${subject} ${isYou ? "steal" : "steals"} from ${victim}`;
    case "silence":
      return `${subject} ${isYou ? "assassinate" : "assassinates"} ${victim}`;
    default:
      return `${subject} ${isYou ? "make" : "makes"} a move`;
  }
}
